import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Link,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { updateReportText, updateStatusReport } from "../../../../../apis";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router";
import { useReportContext } from "../../../ReportContext/useReportContext";
import { useTranslation } from "react-i18next";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import { CloseReport } from "../CloseReport/CloseReport";
import { DetailsClosed } from "../CloseReport/DetailsClosed";
import { EditDetails } from "./EditDetails";
import { StatusComment } from "./StatusComment";
import { StatusObservations } from "./StatusObservations";
import { AddInvestigation } from "../../Investigation2/Modals/AddInvestigation";
import { getSafeParsedItem, initialCurrentReportLS } from "../../ReportViewer";

export const CommentsAndStatus = ({
  id,
  isLoading,
  setIsLoading,
  status,
  setStatus,
  closeModalConfirm,
  interstateNavigation,
  statuses,
  reportRender,
  reportDetail,
  currentReportStatus,
  openModalConfirm,
  selectAllTask,
  checkboxes,
  setActiveTab,
}) => {
  // ---> Custom Hooks <--- //
  const navigate = useNavigate();
  const theme = useTheme();
  const [t] = useTranslation("report");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { status_locales } = useReportContext();

  // ---> Initial States <--- //
  // Open Dialog to edit observations
  const [editingObs, setEditingObs] = useState(false);
  // Dialog Title
  const [editType, setEditType] = useState("");
  // Observation to complement the status
  const [commentStatus, setCommentStatus] = useState("");
  // Details about report status change
  const [message, setMessage] = useState("");
  // Add comment (message)
  const [addMessage, setAddMessage] = useState(false);
  // Show inputs report status
  const [showInput, setShowInput] = useState(null);
  // Handle edition for closed types
  const [editClosed, setEditClosed] = useState(false);
  // Text to edit
  const [objectToEdit, setSetObjectToEdit] = useState({});

  // Show modal to add investigation
  const [showModalAddInvestigation, setShowModalAddInvestigation] =
    useState(false);

  // -----> Observations & Comments <----- //
  // Main details
  const mainComment =
    reportDetail?.status?.current === currentReportStatus
      ? {
          current: reportDetail?.status?.current,
          comment: reportDetail?.status?.comment || "",
          message: reportDetail?.status?.message || "",
          assigned: reportDetail?.status?.assigned || "",
          sub_status: reportDetail?.status?.sub_status || [],
          name: reportDetail?.status?.user?.name || "",
        }
      : {};

  // Comments (message) filtered in the history
  const filteredComments =
    reportDetail?.status?.history.filter(
      (item) =>
        item?.status === currentReportStatus &&
        item.message &&
        item.message.trim()
    ) || [];

  // Observations (comment) filtered in the history
  const filteredObservations =
    reportDetail?.status?.history.filter(
      (item) =>
        item?.status === currentReportStatus &&
        item.comment &&
        item.comment.trim()
    ) || [];

  // Open dialog for text editing
  const hdlEditCurrentText = (object, type) => {
    setSetObjectToEdit(object);
    setEditingObs(true);
    setEditType(type);
    if (type === "observation") {
      setCommentStatus(object.comment);
    } else if (type === "comment") {
      setMessage(object.message);
    }
  };

  // Closes text editing
  const hdlCloseEditObs = () => {
    setEditingObs(false);
    setCommentStatus("");
    setMessage("");
    setSetObjectToEdit({});
  };

  //  Create new observation (comment)
  const handleUpdateObs = async () => {
    setIsLoading(true);
    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    const objToSave = {
      report_id: id,
      status: reportDetail.status.current,
      comment: commentStatus,
      message: "",
      checkboxes,
    };

    try {
      await updateStatusReport(objToSave);
      setShowInput(null);
      setCommentStatus("");
      reportRender();
      enqueueSnackbar(t("Resolution.data_successfully_updated"), {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
    } finally {
      closeSnackbar(sending);
      setIsLoading(false);
    }
  };

  // Modification of observations and comments (comment & message)
  const hdlModifyCurrentText = async () => {
    setIsLoading(true);
    const dataToSend = {
      report_id: id,
      current: objectToEdit.current,
      message,
      comment: commentStatus,
    };

    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      await updateReportText(dataToSend);
      hdlCloseEditObs();
      reportRender();
      enqueueSnackbar(t("Resolution.data_successfully_updated"), {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      closeSnackbar(sending);
    }
  };

  // Deletion of comments and remarks found in the history
  const hdlDeleteText = async (data) => {
    setIsLoading(true);

    const dataToSend = {
      report_id: id,
      status: data.status,
      assigned: data.assigned,
    };

    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      await updateReportText(dataToSend);
      closeModalConfirm();
      reportRender();
      enqueueSnackbar(t("Resolution.data_successfully_updated"), {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      closeSnackbar(sending);
    }
  };

  // -----> Change of status <----- //
  // All report status options
  const statusOptions = Object.entries(status_locales)
    .filter(([key]) => key !== "draft")
    .map(([key, value]) => ({
      key,
      label: value.label,
      color: value.color,
      description: value.description,
    }));

  // Options to show in status change
  const filteredOptions = statusOptions.filter((option) => {
    if (
      reportDetail?.status?.current === "closed" &&
      option.key === "closing_process" &&
      !reportDetail?.status?.details
    )
      return false;
    if (option.key === "closed") return false;
    return option.key !== reportDetail?.status?.current;
  });

  // Update status report
  const handleUpdateStatus = async () => {
    setIsLoading(true);
    const isDenied = statuses.includes(status);
    // if (!selectAllTask) {
    //   alert(t("Care.before_changing_status_select_substatus_checkboxes"));
    //   setIsLoading(false);
    //   closeModalConfirm();
    //   return;
    // } else
    if (isDenied) {
      enqueueSnackbar(t("Resolution.report_not_found"), {
        variant: "error",
        preventDuplicate: true,
        autoHideDuration: 5000,
      });
      setIsLoading(false);
      closeModalConfirm();
      navigate("/reports/all");
    }

    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    const objToSave = {
      report_id: id,
      status: status === "closing_process" ? "closed" : status,
      comment: "",
      message,
      checkboxes,
      ...(reportDetail?.status?.details && {
        details: reportDetail.status.details,
      }),
    };
    try {
      await updateStatusReport(objToSave).then((resp) => {
        setShowInput(null);
        setCommentStatus("");
        setMessage("");
        closeModalConfirm();
        interstateNavigation(status === "closing_process" ? "closed" : status);
        reportRender();
        enqueueSnackbar(t("Resolution.data_successfully_updated"), {
          variant: "success",
        });
      });
    } catch (error) {
      console.error(error);
    } finally {
      closeSnackbar(sending);
      setIsLoading(false);
    }
  };

  return (
    <>
      <AddInvestigation
        show={showModalAddInvestigation}
        onHide={() => setShowModalAddInvestigation(false)}
        id={id}
        reportRender={reportRender}
        callBack={() => {
          setActiveTab("Investigación");
          const currentReportLS =
            getSafeParsedItem("current-report") ?? initialCurrentReportLS(id);
          const newCurrentReportLS = {
            ...currentReportLS,
            tab: "Investigación",
          };
          localStorage.setItem(
            "current-report",
            JSON.stringify(newCurrentReportLS)
          );
          const newlsitem = localStorage.getItem("current-report");
        }}
      />
      {/* Modal for text editing  */}
      <EditDetails
        editingObs={editingObs}
        editType={editType}
        commentStatus={commentStatus}
        setCommentStatus={setCommentStatus}
        message={message}
        setMessage={setMessage}
        isLoading={isLoading}
        hdlModifyCurrentText={hdlModifyCurrentText}
        objectToEdit={objectToEdit}
        hdlCloseEditObs={hdlCloseEditObs}
      />

      {/* Details about report status change from DB */}
      {(mainComment?.message?.trim() ||
        (filteredComments?.length ?? 0) > 0) && (
        <StatusComment
          reportDetail={reportDetail}
          currentReportStatus={currentReportStatus}
          mainComment={mainComment}
          filteredComments={filteredComments}
          hdlEditCurrentText={hdlEditCurrentText}
          openModalConfirm={openModalConfirm}
          hdlDeleteText={hdlDeleteText}
          closeModalConfirm={closeModalConfirm}
        />
      )}

      {/* Action buttons */}
      {reportDetail?.status?.current === currentReportStatus && (
        <>
          <Stack
            display="flex"
            alignItems="center"
            justifyContent={{ xs: "center", sm: "start" }}
            flexDirection="row"
            flexWrap={{ xs: "wrap", md: "nowrap" }}
            spacing={2}
            mt={0.5}
          >
            {/* Add observations */}
            {reportDetail?.status?.current !== "draft" &&
              reportDetail?.status?.current !== "closed" && (
                <EGButton
                  label={t("Care.observations")}
                  iconName="zoom_in"
                  iconSize={20}
                  onClick={() => setShowInput("observations")}
                  disabled={showInput === "observations"}
                />
              )}
            {/* Create a new investigation */}
            {reportDetail?.status?.current === "process" && (
              <EGButton
                label={t("Care.investigate")}
                iconName="outline_search_user"
                iconSize={20}
                onClick={() => setShowModalAddInvestigation(true)}
              />
            )}
            {reportDetail?.status?.current === "closed" &&
              !reportDetail?.status?.details && (
                <EGButton
                  label={t("Care.close_report")}
                  iconName="outline_lock_circle"
                  iconSize={20}
                  onClick={() => setShowInput("closeReport")}
                  disabled={showInput === "closeReport"}
                />
              )}
            {/* Button status change */}
            <EGButton
              label={
                !!reportDetail?.status?.details
                  ? t("Conclusion.reopen")
                  : t("Care.change_status")
              }
              iconName="outline_task_square"
              iconSize={20}
              onClick={() => {
                setShowInput("changeStatusReport");
                setEditClosed(false);
              }}
              disabled={showInput === "changeStatusReport"}
            />
          </Stack>

          {showInput === "closeReport" &&
            (!reportDetail?.status?.details || editClosed) && (
              <CloseReport
                reportDetail={reportDetail}
                reportRender={reportRender}
                setShowInput={setShowInput}
                editClosed={editClosed}
                setEditClosed={setEditClosed}
              />
            )}

          {/* Status change input */}
          {showInput === "changeStatusReport" && (
            <>
              <Autocomplete
                fullWidth
                sx={{ pt: 2 }}
                value={status}
                options={filteredOptions.map((option) => option.key)}
                onChange={(e, value) => setStatus(value)}
                getOptionLabel={(key) => {
                  const option = statusOptions.find((opt) => opt.key === key);
                  return option ? option.label : "";
                }}
                disableClearable
                isOptionEqualToValue={(option, value) => option === value}
                renderOption={(props, key) => {
                  const option = statusOptions.find((opt) => opt.key === key);
                  return (
                    <Paper
                      elevation={0}
                      sx={{
                        border: `solid 1px ${theme.palette.blueGray50.main}`,
                        borderLeft: `7px solid ${option.color}`,
                        borderRadius: "6px",
                        my: 0.33,
                        mx: 1,
                      }}
                      {...props}
                    >
                      <Typography variant="h6" color={option.color} px={1}>
                        {option.label}
                        <Typography variant="body2" color={"secondary"}>
                          {option.description}
                        </Typography>
                      </Typography>
                    </Paper>
                  );
                }}
                renderInput={(params) => (
                  <EGInput
                    {...params}
                    label={
                      status
                        ? status === reportDetail?.status?.current
                          ? `${t("Care.select_new_status")}:`
                          : `${t("Care.change_status")} ${t("Care.to")} "${
                              status_locales[status].label
                            }"`
                        : t("Care.select_option_update_report_status")
                    }
                    variant="outlined"
                  />
                )}
              />

              {/* Add details about report status change */}
              {status !== reportDetail?.status?.current && (
                <Stack textAlign="end" py={1}>
                  {addMessage ? (
                    <EGInput
                      label={`${t("Care.report_status_details")}:`}
                      placeholder={t("Care.enter_details_about_status_change")}
                      sx={{ mt: 1 }}
                      multiline
                      rows={2}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  ) : (
                    <Link onClick={() => setAddMessage(true)} underline="hover">
                      + {t("Care.add_comment")}
                    </Link>
                  )}
                </Stack>
              )}

              {/* Confirmation actions */}
              <Stack
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                flexDirection="row"
                spacing={2}
                pt={1}
              >
                <EGButton
                  label={t("Care.update_status")}
                  iconName="outline_refresh_tick"
                  iconSize={21}
                  disabled={status === reportDetail?.status?.current}
                  onClick={() =>
                    openModalConfirm({
                      variant: "warning",
                      title: t("Care.warning"),
                      titleColor: theme.palette.pink.main,
                      description1: t("Care.alert_change"),
                      question: t("Care.do_you_want_continue"),
                      leftButtonText: t("Care.cancel"),
                      onClickLeft: () => closeModalConfirm(),
                      rightButtonText: t("Care.confirm"),
                      onClickRight: () => handleUpdateStatus(),
                      ethiquin: "progress",
                    })
                  }
                  color={
                    status === reportDetail?.status?.current
                      ? "light"
                      : "success"
                  }
                  iconColor={
                    status !== reportDetail?.status?.current && "white"
                  }
                  labelColor={
                    status !== reportDetail?.status?.current && "white"
                  }
                  iconColorHover={
                    status !== reportDetail?.status?.current && "white"
                  }
                />
                <EGButton
                  label={t("Care.cancel")}
                  iconName="close_icon"
                  iconSize={21}
                  onClick={() => {
                    setShowInput(null);
                    setStatus(reportDetail?.status?.current || "");
                    setAddMessage(false);
                    setMessage("");
                  }}
                />
              </Stack>
            </>
          )}

          {/* Observations */}
          {showInput === "observations" && (
            <>
              <Stack py={2}>
                <EGInput
                  type="textarea"
                  label={`${t("Care.observation")}:`}
                  value={commentStatus}
                  onChange={(e) => setCommentStatus(e.target.value)}
                  placeholder={t(
                    "Care.enter_comment_complement_investigation_status"
                  )}
                  multiline
                  rows={3}
                />
              </Stack>
              <Stack
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                flexDirection="row"
                spacing={2}
              >
                <EGButton
                  label={t("Care.save")}
                  iconName={
                    commentStatus.trim() === ""
                      ? "mark_check"
                      : "mark_check_bold"
                  }
                  disabled={commentStatus.trim() === ""}
                  onClick={handleUpdateObs}
                  color={commentStatus.trim() === "" ? "light" : "success"}
                  iconColor={commentStatus.trim() === "" ? "" : "white"}
                  iconColorHover={commentStatus.trim() === "" ? "" : "white"}
                  labelColor={commentStatus.trim() === "" ? "" : "white"}
                  loading={isLoading}
                />
                <EGButton
                  label={t("Care.cancel")}
                  iconName="close_icon"
                  iconSize={21}
                  onClick={() => {
                    setShowInput(null);
                    setCommentStatus("");
                  }}
                  loading={isLoading}
                />
              </Stack>
            </>
          )}
        </>
      )}

      {/* Observations about report status */}
      {(mainComment?.comment?.trim() ||
        (filteredObservations?.length ?? 0) > 0) && (
        <StatusObservations
          reportDetail={reportDetail}
          currentReportStatus={currentReportStatus}
          mainComment={mainComment}
          filteredObservations={filteredObservations}
          hdlEditCurrentText={hdlEditCurrentText}
          openModalConfirm={openModalConfirm}
          hdlDeleteText={hdlDeleteText}
          closeModalConfirm={closeModalConfirm}
        />
      )}

      {/* Details of closed type */}
      {!!reportDetail?.status?.details && !editClosed && (
        <Box mt={2}>
          <DetailsClosed
            reportDetail={reportDetail}
            setEditClosed={setEditClosed}
            setShowInput={setShowInput}
          />
        </Box>
      )}
    </>
  );
};
